import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateApprovalPath } from '@/modules/settings/approvals/apiHooks';
import { CreateApprovalPath } from '@/modules/settings/approvals/components/Operations/CreateApprovalPath/Step1';
import { useTechnicalApprovals } from '@/modules/settings/approvals/components/Operations/logic';
import EditableApproverRows from '@/modules/settings/approvals/components/shared/ApprovalDetails/ApproverUsersList/EditableApproverRows';
import ProgressBar from '@/modules/settings/approvals/components/shared/ProgressBar';
import { IApprovals } from '@/modules/settings/approvals/types';
import { Button } from '@/shared/components/atoms';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const CreateApprovalPathWrapper = () => {
  const { t } = useTranslation();

  const { closeModal } = useModal();

  const [isFarm, setIsFarm] = useState(false);

  const [step, setStep] = useState(1);

  const { validationCreateApprovalPathSchema } = useTechnicalApprovals();

  const methods = useForm<IApprovals.ICreateApprovalPathFormSchema>({
    resolver: yupResolver(validationCreateApprovalPathSchema(isFarm)),
    mode: 'all',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      approvers: [
        {
          approverId: null,
          canEdit: 1,
        },
      ],
    },
  });

  const { mutate: mutateCreateApprovalPath } = useCreateApprovalPath(t);

  const submitHandler: SubmitHandler<
    IApprovals.ICreateApprovalPathFormSchema
  > = (values) => {
    const { cropsIds: _, ...obj } = values;
    mutateCreateApprovalPath(obj, {
      onSuccess: () => {
        closeModal();
      },
    });
  };

  const handleStepChange = async () => {
    const isFirstStepValid = await methods.trigger([
      'approvalPathTypeId',
      'approvalPathJobOrderTypeId',
      'approvalPathFields',
      'entityId',
      'name',
      'notes',
    ]);
    if (isFirstStepValid) setStep(2);
  };

  return (
    <FormProvider {...methods}>
      <form
        className='create-approval-modal flex w-full flex-col gap-24 px-16 tablet:w-[652px]'
        onSubmit={methods.handleSubmit(submitHandler)}
      >
        <ProgressBar step={step} />
        {step === 1 ? (
          <CreateApprovalPath isFarm={isFarm} setIsFarm={setIsFarm} />
        ) : (
          <EditableApproverRows approvalType='technical' />
        )}
        <div className='flex w-full justify-end'>
          {step === 1 ? (
            <Button onClick={handleStepChange}>{t('next')}</Button>
          ) : (
            <div className='flex gap-2'>
              <Button
                variant='text'
                type='button'
                size='md'
                className='h-56 w-full !rounded-xl border-xs border-grey-200'
                onClick={() => setStep(1)}
              >
                {t('back')}
              </Button>
              <Button type='submit'>{t('save')}</Button>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateApprovalPathWrapper;
